// event-photos.component.ts
import {Component, Input, OnInit} from '@angular/core';
import {EventCommentDTO, EventPhotoDTO, EventPhotoWithComments, EventService} from '../api/event-api.service';
import {ToastrService} from 'ngx-toastr';
import {SpinnerService} from '../shared/services/spinner.service';
import {finalize} from 'rxjs/operators';

@Component({
    selector: 'app-event-photos',
    templateUrl: './event-photos.component.html',
    styleUrls: ['./event-photos.component.css']
})
export class EventPhotosComponent implements OnInit {
    @Input() eventId!: string;

    photos: EventPhotoWithComments[] = [];
    newPhotoUrl = '';
    newPhotoDescription = '';
    newComment = '';
    selectedPhoto?: EventPhotoWithComments;

    constructor(
        private eventService: EventService,
        private toastr: ToastrService,
        private spinner: SpinnerService
    ) {
    }

    ngOnInit(): void {
        this.loadPhotos();
    }

    loadPhotos() {
        this.spinner.show();
        this.eventService.getPhotos(this.eventId).pipe(
            finalize(() => this.spinner.hide())
        ).subscribe({
            next: (res: EventPhotoDTO[]) => {
                if (res && Array.isArray(res)) {
                    this.photos = res.map(p => ({...p, comments: []}));
                    this.photos.forEach(p => this.loadComments(p));
                } else {
                    this.photos = [];
                }
            },
            error: () => {
                this.photos = [];
            }
        });
    }

    loadComments(photo: EventPhotoWithComments) {
        if (!photo.photoId) return;
        this.eventService.getComments(photo.photoId).subscribe(comments => {
            photo.comments = comments;
        });
    }

    uploadPhoto() {
        if (!this.newPhotoUrl) return;
        const photo: EventPhotoDTO = {
            url: this.newPhotoUrl,
            description: this.newPhotoDescription
        };
        this.spinner.show();
        this.eventService.uploadPhoto(this.eventId, photo).subscribe({
            next: res => {
                this.photos.push({...res, comments: []});
                this.newPhotoUrl = '';
                this.newPhotoDescription = '';
                this.toastr.success('Photo uploaded');
                this.spinner.hide();
            },
            error: () => this.spinner.hide()
        });
    }

    moderatePhoto(photo: EventPhotoWithComments, status: boolean) {
        if (!photo.photoId) return;
        this.spinner.show();
        this.eventService.moderatePhoto(this.eventId, photo.photoId, status ? 'approved' : 'rejected')
            .subscribe({
                next: res => {
                    photo.moderated = res.moderated;
                    this.toastr.success('Photo moderation updated');
                    this.spinner.hide();
                },
                error: () => this.spinner.hide()
            });
    }

    addComment(photo: EventPhotoWithComments) {
        if (!photo.photoId || !this.newComment.trim()) return;
        const comment: EventCommentDTO = {comment: this.newComment, userId: 'currentUserId'};
        this.spinner.show();
        this.eventService.addComment(photo.photoId, comment).subscribe({
            next: res => {
                photo.comments?.push(res);
                this.newComment = '';
                this.toastr.success('Comment added');
                this.spinner.hide();
            },
            error: () => this.spinner.hide()
        });
    }

    deletePhoto(photo: EventPhotoWithComments) {
        if (!photo.photoId) return;
        this.spinner.show();
        this.eventService.deletePhoto(this.eventId, photo.photoId).subscribe({
            next: () => {
                this.photos = this.photos.filter(p => p.photoId !== photo.photoId);
                this.toastr.success('Photo deleted');
                this.spinner.hide();
            },
            error: () => this.spinner.hide()
        });
    }
}
